import React from 'react';
import { fmtUnit, fmtUnitRate } from '../../lib/display-labels';

/**
 * What accepting a Directional & MWD quote does to its CREW lines (api#1092),
 * said before the accept writes it.
 *
 * A crew line is priced from the personnel rate card (`source_kind
 * 'dh_personnel_rate'`, unit per_day / standby_day / travel_day). Accept binds
 * ONE customer personnel rate per role over the quote's validity window, at
 * the quoted prices; a rate of that role the quote did not price stays NULL
 * and prices at the role's standard rate. After accept the bound rows are
 * listed by QuoteCreatedRecordsPanel, from the API, so this panel is shown only
 * before it.
 *
 * Crew lines exist only when the tenant's Directional & MWD mode was on when
 * the quote was priced (the API lists them only then), so a quote without
 * them renders nothing here. Every figure is the line's own; nothing is summed.
 */
export interface CrewLine {
  id: string;
  description?: string;
  unit?: string;
  rate: number | string;
  quantity: number | string;
  source_kind?: string | null;
}

export const isCrewLine = (l: { source_kind?: string | null }) => l.source_kind === 'dh_personnel_rate';

/**
 * Rates print as the quote's own line table prints them (fmtUnitRate with no
 * currency code), so one page does not spell one figure two ways.
 */
export function QuoteCrewBindingPanel({ lines }: { lines: CrewLine[] }) {
  const crew = lines.filter(isCrewLine);
  if (crew.length === 0) return null;
  return (
    <section
      data-testid="quote-crew-binding"
      className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
    >
      <div className="border-b border-fieldiq-border px-4 py-3">
        <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Directional &amp; MWD crew</p>
        <h2 className="mt-1 text-base font-extrabold tracking-tight text-white">Crew rates bound on accept</h2>
        <p className="mt-1 text-xs text-fieldiq-muted">
          Accepting sets one crew rate per role for this customer, for the quote&apos;s validity window, at these prices. A rate of a
          role that is not on the quote stays at the standard rate. If this customer already has crew rates for these roles in that
          window, accept is refused and says which.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-left" aria-label="Crew lines">
          <thead className="bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            <tr>
              <th scope="col" className="px-4 py-2">Crew rate</th>
              <th scope="col" className="px-4 py-2">Unit</th>
              <th scope="col" className="px-4 py-2 text-right">Rate</th>
            </tr>
          </thead>
          <tbody>
            {crew.map((l) => (
              <tr key={l.id} className="border-t border-fieldiq-border">
                <td className="px-4 py-2 text-sm font-medium text-white">{l.description || '—'}</td>
                <td className="px-4 py-2 text-sm text-fieldiq-text">{l.unit ? fmtUnit(l.unit) : '—'}</td>
                <td className="px-4 py-2 text-right font-mono text-sm text-fieldiq-text">{fmtUnitRate(l.rate as string)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
